import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FmlxIcon, FmlxProgressBar, FmlxSkeleton, FmlxTooltip} from 'fmlx-common-ui';

import {
  selectHumidifierDoorStatus,
  selectHumidifierRuntime,
  selectIsHumidifierConnected,
  selectIsWaterLevelLow,
} from 'stores/selectors/humidifierSelectors';
import {subscribeEvents, unsubscribeEvents} from 'stores/business/humidifierBusiness';
import SignalRHubs from 'stores/business/hubs/SignalRHubs';
import {HumidifierDoorStatus, SignalRHub} from 'constants/dataEnum';

import './HumidifierRuntime.scss';
import {useIntl} from 'react-intl';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';

function HumidifierRuntime() {
  const hubRef = useRef(null);
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();

  const isHumidifierConnected = useSelector(selectIsHumidifierConnected);
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);
  const {
    ambientHumidity,
    ambientTemperature,
    chamberHumidity,
    chamberTemperature,
    isBelowSetPoint: isHumidityBelowSetpoint,
  } = useSelector(selectHumidifierRuntime);
  const humidifierDoorStatus = useSelector(selectHumidifierDoorStatus);
  const isWaterLevelLow = useSelector(selectIsWaterLevelLow);
  const isDoorOpen = humidifierDoorStatus === HumidifierDoorStatus.OPEN;

  const runtimeFields = [
    {label: 'Ambient', value: ambientHumidity, temp: ambientTemperature, isChamber: false},
    {label: 'Chamber', value: chamberHumidity, temp: chamberTemperature, isChamber: true},
  ];

  const renderValue = (value, suffix = '') =>
    value === null || value === undefined ? <FmlxSkeleton width="100%" height="100%" /> : `${value}${suffix}`;

  useEffect(() => {
    if (hubRef.current || !isInstrumentOperational) return undefined;

    const hub = SignalRHubs.getHub(SignalRHub.INSTRUMENT);
    hubRef.current = hub;
    dispatch(subscribeEvents(hub));

    return () => dispatch(unsubscribeEvents(hubRef.current));
  }, [dispatch, isInstrumentOperational]);

  const renderDoorIcon = () =>
    humidifierDoorStatus != null ? (
      <FmlxTooltip title={isDoorOpen ? 'Open' : 'Close'} placement={FmlxTooltip.Placement.TOP}>
        <div className="icon">
          <FmlxIcon
            size={24}
            name={isDoorOpen ? 'FastDoorOpen' : 'FastDoor'}
            customColor={isDoorOpen ? '#FFCA0A' : '#4CAF50'}
          />
        </div>
      </FmlxTooltip>
    ) : (
      <FmlxProgressBar
        type={FmlxProgressBar.Type.CIRCULAR}
        variant={FmlxProgressBar.Variant.INDETERMINATE}
        size={FmlxProgressBar.Size.SMALL}
      />
    );

  const renderWaterLevelLowIcon = () =>
    isWaterLevelLow != null ? (
      <FmlxTooltip
        title={isWaterLevelLow ? 'Water level is low' : 'Water level is normal'}
        placement={FmlxTooltip.Placement.TOP}
      >
        <div className="icon">
          <FmlxIcon size={24} name="Dispense" customColor={isWaterLevelLow ? '#FF5252' : '#4CAF50'} />
        </div>
      </FmlxTooltip>
    ) : (
      <FmlxProgressBar
        type={FmlxProgressBar.Type.CIRCULAR}
        variant={FmlxProgressBar.Variant.INDETERMINATE}
        size={FmlxProgressBar.Size.SMALL}
      />
    );

  const renderDisconnectedIcon = () => (
    <FmlxTooltip
      title={formatMessage({id: 'statusBar.humidifierDisconnected.tooltip'})}
      placement={FmlxTooltip.Placement.TOP}
    >
      <div className="icon">
        <FmlxIcon size={24} name="Cancel" customColor="#FF5252" />
      </div>
    </FmlxTooltip>
  );

  return (
    <div className="humidifier-runtime">
      {runtimeFields.map(({label, value, temp, isChamber}) => {
        const isWarning = isChamber && isHumidityBelowSetpoint;

        return (
          <div className="humidifier-runtime__block" key={label}>
            <div className="left">
              <div className="label">{label}</div>
              <span className="temp">
                {isHumidifierConnected && isInstrumentOperational ? renderValue(temp, ' ℃') : '-'}
              </span>
            </div>
            <span className={`value ${isWarning ? 'warning' : ''}`}>
              {isHumidifierConnected && isInstrumentOperational ? renderValue(value, '%') : '-'}
            </span>
          </div>
        );
      })}

      <div className="runtime-status-icon">
        {isHumidifierConnected && isInstrumentOperational ? renderDoorIcon() : renderDisconnectedIcon()}
      </div>
      <div className="runtime-status-icon">
        {isHumidifierConnected && isInstrumentOperational ? renderWaterLevelLowIcon() : renderDisconnectedIcon()}
      </div>
    </div>
  );
}

export default HumidifierRuntime;
